<template>
  <div id="heroCarousel" class="carousel slide d-flex align-items-center p-0" data-bs-ride="carousel">
    <!-- Loading state -->
    <div v-if="loading" class="container h-100 d-flex align-items-center justify-content-center">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Carousel content -->
    <template v-else-if="slides.length > 0">
      <!-- Carousel indicators -->
      <div class="carousel-indicators pt-0 mt-0">
        <button 
          v-for="(slide, index) in slides" 
          :key="slide.id" 
          type="button" 
          data-bs-target="#heroCarousel" 
          :data-bs-slide-to="index" 
          :class="{ active: index === 0 }" 
          :aria-current="index === 0 ? 'true' : 'false'"
          :aria-label="`Slide ${index + 1}`">
        </button>
      </div>
      
      <!-- Carousel items -->
      <div class="carousel-inner h-100">
        <div 
          v-for="(slide, index) in slides" 
          :key="slide.id" 
          class="carousel-item h-100" 
          :class="{ active: index === 0 }">
          
          <a :href="slide.link" class="w-100 h-100 d-flex align-items-center justify-content-center carousel-slide-link">
            <img 
              :src="getImageForDevice(slide)" 
              :alt="`Slide ${index + 1}`" 
              class="slide-image w-100" 
            />
          </a>
        </div>
      </div>
    </template>

    <!-- Fallback to static slides -->
    <template v-else>
      <!-- Carousel indicators -->
      <div class="carousel-indicators pt-0 mt-0">
        <button 
          v-for="(slide, index) in staticSlides" 
          :key="index" 
          type="button" 
          data-bs-target="#heroCarousel" 
          :data-bs-slide-to="index" 
          :class="{ active: index === 0 }" 
          :aria-current="index === 0 ? 'true' : 'false'"
          :aria-label="`Slide ${index + 1}`">
        </button>
      </div>
      
      <!-- Carousel items -->
      <div class="carousel-inner h-100">
        <div 
          v-for="(slide, index) in staticSlides" 
          :key="index" 
          class="carousel-item h-100" 
          :class="{ active: index === 0 }">
          
          <div class="container h-100">
            <div class="row h-100 align-items-center">
              <!-- Text content -->
              <div class="col-lg-6 text-column mt-lg-0 mt-5">
                <div class="slide-text-wrapper pt-0">
                  <h2 class="slide-title">{{ slide.title }}</h2>
                  <p class="slide-description fs-4 col-lg-9">{{ slide.description }}</p>
                  <a :href="slide.link" class="btn  btn-primary slide-button">
                    {{ slide.buttonText }}
                  </a>
                </div>
              </div>
              
              <!-- Image -->
              <div class="col-lg-6 image-column d-flex align-items-center pt-0 justify-content-center">
                <div class="slide-image-wrapper pt-0">
                  <img 
                    :src="cleanMediaUrl(slide.image) || slide.image" 
                    :alt="slide.title" 
                    class="slide-image" 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import { getPublicCarouselImages } from '@/services/uploads';
import { cleanMediaUrl } from '@/utils/text';

const slides = ref([]);
const loading = ref(true);
const isMobile = ref(window.innerWidth <= 768);

// Función para obtener la imagen apropiada según el dispositivo
const getImageForDevice = (slide) => {
  let raw;
  if (isMobile.value && slide.mobileImage) {
    raw = slide.mobileImage;
  } else {
    raw = slide.image;
  }
  const u = cleanMediaUrl(raw);
  return u || raw;
};

// Actualizar isMobile en resize
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

// Inicializar Bootstrap Carousel
const initCarousel = () => {
  nextTick(() => {
    const carouselElement = document.getElementById('heroCarousel');
    if (carouselElement && window.bootstrap) {
      // Destruir instancia previa si existe
      const existingCarousel = window.bootstrap.Carousel.getInstance(carouselElement);
      if (existingCarousel) {
        existingCarousel.dispose();
      }
      // Crear nueva instancia
      new window.bootstrap.Carousel(carouselElement, {
        interval: 5000,
        wrap: true,
        keyboard: true,
        pause: 'hover'
      });
    }
  });
};

// Static slides data (fallback)
const staticSlides = [
  {
    title: "Descuento en sustratos",
    description: "Todos los sabados tenemos 10% de descuento en sustratos en nuestro local.",
    image: require("@/assets/images/products/sustratos-hero.png"),
    link: "/productos?categories=sustratos",
    buttonText: "Ver Sustratos"
  },
  {
    title: "Fertilizantes y aditivos",
    image: require("@/assets/images/products/fertilizantes-hero.png"),
    description: "Fertilizantes y aditivos de alta calidad para tus cultivos.",
    link: "/productos?categories=fertilizantes",
    buttonText: "Ver Fertilizantes"
  },
];

const fetchCarouselImages = async () => {
  try {
    loading.value = true;
    const data = await getPublicCarouselImages();
    if (data && data.length > 0) {
      slides.value = data;
    }
  } catch (error) {
    console.error('Error loading carousel images:', error);
    // Si falla, slides.value quedará vacío y se usará staticSlides
  } finally {
    loading.value = false;
    // Inicializar carousel después de cargar las imágenes
    initCarousel();
  }
};

onMounted(() => {
  fetchCarouselImages();
  window.addEventListener('resize', handleResize);
});

// Cleanup
import { onBeforeUnmount } from 'vue';
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
/* Carousel container */
#heroCarousel {
  height: 90vh;
  max-height: 100vh;
  min-height: 500px;
  position: relative;
  overflow: hidden;
  padding-top: 80px;
  width: 100%;
  background: transparent;
}

.carousel-inner,
.carousel-item {
  background: transparent;
}

.carousel-item {
  width: 100%;
  height: 100%;
  position: relative;
}

.carousel-slide-link {
  position: relative;
}

.carousel-slide-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.7) 70%, #000 100%);
  pointer-events: none;
  z-index: 1;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: fadeIn 1s ease-in-out;
}

/* Content alignment and styling */
.slide-text-wrapper {
  padding: 2rem 0;
}

.text-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Text styling */
.slide-title {
  font-size: 5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #ffffff;
  text-align: left;
  animation: slideFromLeft 0.8s forwards;
}

.slide-description {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: rgba(255,255,255,0.9);
  line-height: 1.6;
  animation: slideFromLeft 1s forwards;
  animation-delay: 0.2s;
  opacity: 0;
}

.slide-button {
  display: inline-block;
  padding: 0.75rem 2rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  animation: slideFromLeft 1.2s forwards;
  animation-delay: 0.4s;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

/* Bootstrap carousel indicator styling */
.carousel-indicators {
  position: absolute;
  bottom: 20px;
  z-index: 15;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.carousel-indicators button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.8);
  padding: 0;
  margin: 0;
  transition: all 0.3s ease;
}

.carousel-indicators button.active {
  background-color: #ffffff;
  border-color: #ffffff;
  transform: scale(1.3);
}

/* Animation keyframes */
@keyframes slideFromLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive styles */
@media (max-width: 991.98px) {
  #heroCarousel {
    height: 100vh !important;
    max-height: unset;
    padding-top: 60px;
    padding-bottom: 0;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
  }
  
  .carousel-item {
    height: 100%;
  }
  
  .carousel-slide-link {
    height: 100%;
  }
  
  .carousel-slide-link::after {
    display: block;
    height: 50%;
  }
  
  .slide-image {
    height: 100%;
    max-height: unset;
    object-fit: cover;
    width: 100%;
  }
  
  .slide-title {
    font-size: 2.5rem;
    text-align: center;
  }
  
  .slide-description {
    text-align: center;
  }
  
  .slide-text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem 1rem;
  }
  
  .carousel-item .row {
    flex-direction: column-reverse;
  }
}

@media (max-width: 767.98px) {
  #heroCarousel {
    padding-top: 60px;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);
    width: 100vw;
    overflow-y: auto;
  }

  .carousel-slide-link::after {
    display: block;
    height: 40%;
    background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.8) 60%, #000 100%);
  }

  .slide-image {
    height: 100%;
    max-height: unset;
    width: 100%;
    object-fit: cover;
  }
  
  .slide-title {
    font-size: 2rem;
  }
  
  .slide-description {
    font-size: 1rem;
  }
  
  .carousel-indicators{
    bottom: 10px;
    z-index: 20;
  }
}
</style>
